/**
 * ProceduresDashboard — full-page power dashboard for company procedures.
 *
 * A procedure: name + description + category (Workflow / Accounts /
 * Administrative / Marketing / user-added) + the company/companies it
 * concerns, followed by ordered STEPS. Each step = description + one of:
 *   - a link
 *   - an image (uploaded, rendered inline)
 *   - a document — uploaded standalone OR pulled from an existing
 *     project's documents; flagged as the working 'procedure' file or a
 *     completed 'example'
 *   - a Password Safe entry (only entries of the procedure's companies
 *     that are visible to you)
 *
 * Three views: list → editor (create/edit + step management) → viewer
 * (read-and-follow presentation, also used by the pop-out openers
 * elsewhere in the app via the exported ProcedureViewer).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, HStack, VStack, Input, Select, FormControl, FormLabel, Textarea,
  Box, Text, Badge, Flex, IconButton, useToast, Tag, TagLabel, TagCloseButton,
  Wrap, Tooltip, Spinner, Image, Radio, RadioGroup, Stack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import {
  FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiClipboard, FiKey, FiFile,
  FiImage, FiLink, FiArrowUp, FiArrowDown, FiEye, FiEyeOff, FiUpload, FiFolder, FiRotateCcw,
} from 'react-icons/fi';
import api from '../api';
import PageShell from './PageShell';
import ConfirmDialog from './ConfirmDialog';

const STEP_TYPES = [
  { key: 'text',     label: 'Description only', icon: FiClipboard },
  { key: 'link',     label: 'Link',             icon: FiLink },
  { key: 'image',    label: 'Image',            icon: FiImage },
  { key: 'document', label: 'Document / File',  icon: FiFile },
  { key: 'password', label: 'Password (Safe)',  icon: FiKey },
];


// ── Password step, inline in the viewer ─────────────────────────────────────
// Approved users: eye button reveals the password right here (via the
// safe's secure reveal endpoint — SSO chains and visibility enforced
// server-side); clicking the revealed value copies it. Unapproved users
// are told exactly who can grant access, by real name.
export function PasswordStepInline({ step }) {
  const [shown, setShown] = React.useState(null);   // { username, password } | null
  const busyRef = React.useRef(false);              // double-click guard — NOT a
                                                    // disabled state (see below)
  const toast = useToast();
  const doReveal = async () => {
    if (shown !== null) { setShown(null); return; }
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      // Username comes from the entry detail; password from the secure
      // reveal endpoint (SSO chains + visibility enforced server-side).
      const [det, rev] = await Promise.all([
        api.get(`/password-safe/${step.password_id}`),
        api.post(`/password-safe/${step.password_id}/reveal`),
      ]);
      setShown({ username: det.data?.username || '', password: rev.data?.password || '' });
    } catch (e) {
      toast({ title: 'Could not reveal', description: e?.response?.data?.error || '', status: 'error', duration: 2500 });
    } finally { busyRef.current = false; }
  };
  const copyText = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value || '');
      toast({ title: `${label} copied to clipboard`, status: 'success', duration: 1200 });
    } catch (e) { toast({ title: 'Copy failed', status: 'error', duration: 2000 }); }
  };
  if (step.password_restricted) {
    return (
      <Flex mt={2} gap={2} align="flex-start" bg="red.50" border="1px" borderColor="red.100" borderRadius="md" p={2}>
        <FiKey size={12} style={{ marginTop: 2 }} />
        <Text fontSize="11px" color="red.700">
          Sorry, you are not approved to view this information. Please request access from:{' '}
          <b>{(step.password_approvers || []).join(', ') || 'the entry creator'}</b>
        </Text>
      </Flex>
    );
  }
  return (
    <Flex mt={2} gap={2} align="center" flexWrap="wrap">
      <FiKey size={12} />
      <Text fontSize="11px" color="purple.700" fontWeight="600">Use password: {step.password_name}</Text>
      {/* SCROLL-JUMP FIX — two deliberate choices here:
          1. onMouseDown preventDefault: the button never takes focus, so
             nothing focus-related can scroll the page.
          2. No isLoading/disabled: disabling a focused button mid-click
             made the surrounding pop-out Modal's FocusLock recapture focus
             onto its first focusable element and scroll it into view —
             that was the jump down the list. The busyRef guard above
             debounces double-clicks without ever disabling. */}
      <Tooltip label={shown !== null ? 'Hide' : 'Reveal username & password'}>
        <IconButton size="xs" variant="outline" colorScheme="purple"
          icon={shown !== null ? <FiEyeOff size={11} /> : <FiEye size={11} />}
          aria-label="reveal" onClick={doReveal}
          onMouseDown={e => e.preventDefault()} />
      </Tooltip>
      {shown !== null && (
        <HStack spacing={2} flexWrap="wrap">
          <Tooltip label="Click to copy username">
            <Text fontSize="11px" fontFamily="mono" bg="purple.50" px={2} borderRadius="md"
              cursor="pointer" onClick={() => copyText('Username', shown.username)}>
              {shown.username || '(no username)'}
            </Text>
          </Tooltip>
          <Tooltip label="Click to copy password">
            <Text fontSize="11px" fontFamily="mono" bg="yellow.50" px={2} borderRadius="md"
              cursor="pointer" onClick={() => copyText('Password', shown.password)}>
              {shown.password || '(empty)'}
            </Text>
          </Tooltip>
        </HStack>
      )}
    </Flex>
  );
}

// ── Read-and-follow viewer. Exported so invoice/task/company pop-outs can
// render a procedure without the dashboard chrome.
export function ProcedureViewer({ procedure }) {
  if (!procedure) return null;
  return (
    <VStack align="stretch" spacing={3}>
      {procedure.description && (
        <Text fontSize="13px" color="gray.700" whiteSpace="pre-wrap">{procedure.description}</Text>
      )}
      {(procedure.companies || []).length > 0 && (
        <Wrap spacing={1}>
          {procedure.companies.map(c => <Badge key={c.id} colorScheme="blue" fontSize="9px">{c.name}</Badge>)}
        </Wrap>
      )}
      {(procedure.steps || []).map((s, i) => (
        <Flex key={s.id} gap={3} align="flex-start" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
          <Flex w="26px" h="26px" borderRadius="full" bg="teal.500" color="white" align="center"
            justify="center" fontSize="12px" fontWeight="800" flexShrink={0}>{i + 1}</Flex>
          <Box flex={1} minW={0}>
            {s.title && <Text fontSize="13px" fontWeight="700" mb={0.5}>{s.title}</Text>}
            {s.description && <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap">{s.description}</Text>}
            {s.step_type === 'link' && s.link_url && (
              <Button as="a" href={s.link_url} target="_blank" rel="noopener noreferrer" size="xs" mt={2}
                leftIcon={<FiExternalLink size={11} />} variant="outline" colorScheme="blue">
                {s.link_url.length > 60 ? s.link_url.slice(0, 60) + '…' : s.link_url}
              </Button>
            )}
            {s.step_type === 'image' && s.file_url && (
              <Image src={s.file_url} alt={s.file_name || 'step image'} mt={2} maxH="320px" borderRadius="md"
                border="1px" borderColor="gray.200" />
            )}
            {s.step_type === 'document' && (s.file_url || s.document_id) && (
              <HStack mt={2} spacing={2}>
                <Badge colorScheme={s.doc_kind === 'example' ? 'orange' : 'blue'} fontSize="8px">
                  {s.doc_kind === 'example' ? 'EXAMPLE' : 'PROCEDURE DOC'}
                </Badge>
                {s.file_url
                  ? <Button as="a" href={s.file_url} target="_blank" rel="noopener noreferrer" size="xs"
                      leftIcon={<FiFile size={11} />} variant="outline">{s.file_name || 'Open file'}</Button>
                  : <Text fontSize="11px" color="gray.600"><FiFolder size={11} style={{ display: 'inline', verticalAlign: '-1px' }} /> {s.document_name || 'Project document'}</Text>}
              </HStack>
            )}
            {s.step_type === 'password' && <PasswordStepInline step={s} />}
          </Box>
        </Flex>
      ))}
      {(procedure.steps || []).length === 0 && (
        <Text fontSize="12px" color="gray.400" fontStyle="italic">No steps yet.</Text>
      )}
    </VStack>
  );
}

const emptyStepForm = () => ({
  title: '', description: '', step_type: 'text', link_url: '',
  file_id: '', file_name: '', document_id: '', document_name: '',
  doc_kind: 'procedure', password_entry_id: '', doc_source: 'upload', project_id: '',
});

export default function ProceduresDashboard({ fullPage, onBack, isOpen, onClose, initialEditId, initialViewId }) {
  const active = fullPage || isOpen;
  const [procedures, setProcedures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState('');
  const [versionView, setVersionView] = useState('current'); // current | previous | deleted
  const [companyFilter, setCompanyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list');           // list | edit | view
  const [current, setCurrent] = useState(null);       // full procedure (edit/view)
  const [form, setForm] = useState(null);             // header form during edit
  const [newCatName, setNewCatName] = useState('');
  const [stepForm, setStepForm] = useState(null);     // step being added/edited
  const [editingStepId, setEditingStepId] = useState(null);
  const [viewStep, setViewStep] = useState(null);      // single-step viewer pop-up
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectDocs, setProjectDocs] = useState([]);
  const [safeEntries, setSafeEntries] = useState([]);
  const toast = useToast();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('view', versionView);
      if (catFilter) params.set('category_id', catFilter);
      if (companyFilter) params.set('company_id', companyFilter);
      const { data } = await api.get(`/procedures?${params}`);
      setProcedures(Array.isArray(data) ? data : []);
    } catch (e) { setProcedures([]); }
    finally { setLoading(false); }
  }, [catFilter, companyFilter, versionView]);

  useEffect(() => {
    if (!active) return;
    fetchList();
    api.get('/procedures/categories').then(r => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/companies').then(r => { const d = r.data?.data || r.data; setCompanies(Array.isArray(d) ? d : []); }).catch(() => {});
  }, [active, fetchList]);

  // Deep-link straight into editing or viewing a specific procedure
  // (task-modal Edit pencil / superseded links) — as a proper page.
  useEffect(() => {
    if (active && initialEditId) openEdit({ id: initialEditId });
    else if (active && initialViewId) openView({ id: initialViewId });
    // eslint-disable-next-line
  }, [active, initialEditId, initialViewId]);

  const loadProcedure = async (id) => {
    const { data } = await api.get(`/procedures/${id}`);
    setCurrent(data);
    return data;
  };

  const openView = async (p) => { try { await loadProcedure(p.id); setView('view'); } catch (e) {} };
  const openEdit = async (p) => {
    try {
      const data = p ? await loadProcedure(p.id) : null;
      setForm({
        name: data?.name || '', description: data?.description || '',
        category_id: data?.category_id || '',
        companies: (data?.companies || []).map(c => ({ id: c.id, name: c.name })),
      });
      if (!p) setCurrent(null);
      setStepForm(null); setNewCatName('');
      setView('edit');
    } catch (e) {}
  };

  const saveHeader = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required', status: 'warning', duration: 2000 }); return null; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description,
        category_id: form.category_id || null,
        company_ids: form.companies.map(c => c.id),
      };
      let id = current?.id;
      if (id) await api.put(`/procedures/${id}`, payload);
      else { const { data } = await api.post('/procedures', payload); id = data.id; }
      const data = await loadProcedure(id);
      toast({ title: 'Procedure saved', status: 'success', duration: 1500 });
      fetchList();
      return data;
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.error || '', status: 'error', duration: 2500 }); return null; }
    finally { setSaving(false); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const { data } = await api.post('/procedures/categories', { name: newCatName.trim() });
      const { data: cats } = await api.get('/procedures/categories');
      setCategories(Array.isArray(cats) ? cats : []);
      setForm(p => ({ ...p, category_id: data.id }));
      setNewCatName('');
    } catch (e) { toast({ title: 'Could not add category', status: 'error', duration: 2000 }); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteProcedure = (p) => setDeleteTarget(p);
  const confirmDeleteProcedure = async () => {
    const p = deleteTarget; if (!p) return;
    try { await api.delete(`/procedures/${p.id}`); fetchList(); toast({ title: 'Deleted (retained for audit)', status: 'info', duration: 1500 }); } catch (e) {}
    setDeleteTarget(null);
  };

  // ── Step form support data ──
  const openStepForm = async (existing) => {
    setEditingStepId(existing?.id || null);
    setStepForm(existing ? {
      title: existing.title || '', description: existing.description || '',
      step_type: existing.step_type || 'text', link_url: existing.link_url || '',
      file_id: existing.file_id || '', file_name: existing.file_name || '',
      document_id: existing.document_id || '', document_name: existing.document_name || '',
      doc_kind: existing.doc_kind || 'procedure', password_entry_id: existing.password_id || '',
      doc_source: existing.document_id ? 'project' : 'upload', project_id: '',
    } : emptyStepForm());
    api.get('/projects').then(r => { const d = r.data?.data || r.data; setProjects(Array.isArray(d) ? d : []); }).catch(() => {});
    // Password picker (0.9.0.32): only entries that belong with this
    // procedure — attached to a task/sub-task the procedure is on, or
    // company-wide entries of the procedure's companies (which the server
    // fills in from those tasks). Server-side list, server-side visibility.
    try {
      if (current?.id) {
        const r = await api.get(`/procedures/${current.id}/password-options`);
        setSafeEntries(Array.isArray(r.data) ? r.data : []);
        // The server may have folded task companies into the procedure —
        // mirror them into the editor's company list.
        const fresh = await api.get(`/procedures/${current.id}`).then(x => x.data).catch(() => null);
        if (fresh?.companies) setForm(f => f ? ({ ...f, companies: [...f.companies,
          ...fresh.companies.filter(c => !f.companies.some(x => String(x.id) === String(c.id))).map(c => ({ id: c.id, name: c.name }))] }) : f);
      } else setSafeEntries([]);
    } catch (e) { setSafeEntries([]); }
  };

  useEffect(() => {
    if (!stepForm?.project_id) { setProjectDocs([]); return; }
    api.get(`/documents?project_id=${stepForm.project_id}`)
      .then(r => setProjectDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjectDocs([]));
  }, [stepForm?.project_id]);

  const uploadStepFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'documents');
      fd.append('entity_type', 'procedure_step');
      const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStepForm(p => ({ ...p, file_id: data.id, file_name: data.original_name || file.name }));
      return data;
    } catch (e) { toast({ title: 'Upload failed', description: e?.response?.data?.error || '', status: 'error', duration: 2500 }); return null; }
    finally { setUploading(false); }
  };

  // ── Paste-a-snapshot ──────────────────────────────────────────────────
  // Ctrl+V a screenshot anywhere in the step form (or into the dashed
  // paste box) and it uploads straight away as the step's image — no
  // save-as-file-then-upload dance. Pasting an image while the step is
  // still 'text' switches it to an image step automatically; pasted images
  // on a 'document' step attach as the document file.
  const handleStepPaste = async (e) => {
    if (!stepForm) return;
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) continue;
        e.preventDefault();
        const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
        const file = new File([blob], `snapshot-${stamp}.${ext}`, { type: item.type });
        if (stepForm.step_type === 'text') setStepForm(p => ({ ...p, step_type: 'image' }));
        const up = await uploadStepFile(file);
        if (up) toast({ title: 'Snapshot pasted & saved as image', status: 'success', duration: 1800 });
        return;
      }
    }
  };

  const addStep = async () => {
    let proc = current;
    if (!proc) { proc = await saveHeader(); if (!proc) return; }   // must exist first
    const sf = stepForm;
    if (!sf.description.trim() && !sf.title.trim()) { toast({ title: 'Add a description', status: 'warning', duration: 2000 }); return; }
    if (sf.step_type === 'link' && !sf.link_url.trim()) { toast({ title: 'Add the link URL', status: 'warning', duration: 2000 }); return; }
    if (sf.step_type === 'image' && !sf.file_id) { toast({ title: 'Upload the image', status: 'warning', duration: 2000 }); return; }
    if (sf.step_type === 'document' && !sf.file_id && !sf.document_id) { toast({ title: 'Upload or pick a document', status: 'warning', duration: 2000 }); return; }
    if (sf.step_type === 'password' && !sf.password_entry_id) { toast({ title: 'Choose a password entry', status: 'warning', duration: 2000 }); return; }
    setSaving(true);
    try {
      const payload = {
        title: sf.title, description: sf.description, step_type: sf.step_type,
        link_url: sf.link_url || null,
        file_id: sf.file_id || null,
        document_id: sf.doc_source === 'project' ? (sf.document_id || null) : null,
        doc_kind: sf.doc_kind,
        password_entry_id: sf.password_entry_id || null,
      };
      if (editingStepId) await api.put(`/procedures/${proc.id}/steps/${editingStepId}`, payload);
      else await api.post(`/procedures/${proc.id}/steps`, payload);
      const fresh = await loadProcedure(proc.id);
      // A password step may have attached its company to the procedure —
      // reflect that in "Companies involved" without losing unsaved picks.
      if (fresh?.companies) setForm(f => f ? ({ ...f, companies: [...f.companies,
        ...fresh.companies.filter(c => !f.companies.some(x => String(x.id) === String(c.id))).map(c => ({ id: c.id, name: c.name }))] }) : f);
      setStepForm(null); setEditingStepId(null);
      fetchList();
    } catch (e) { toast({ title: editingStepId ? 'Could not save step' : 'Could not add step', description: e?.response?.data?.error || '', status: 'error', duration: 2500 }); }
    finally { setSaving(false); }
  };

  const moveStep = async (idx, dir) => {
    const steps = [...(current?.steps || [])];
    const j = idx + dir;
    if (j < 0 || j >= steps.length) return;
    [steps[idx], steps[j]] = [steps[j], steps[idx]];
    try {
      await api.put(`/procedures/${current.id}/steps-reorder`, { step_ids: steps.map(s => s.id) });
      await loadProcedure(current.id);
    } catch (e) {}
  };

  const deleteStep = async (s) => {
    try { await api.delete(`/procedures/${current.id}/steps/${s.id}`); await loadProcedure(current.id); fetchList(); } catch (e) {}
  };

  // ── Viewer ──
  if (view === 'view' && current) {
    return (
      <PageShell fullPage={fullPage} onBack={() => { setView('list'); setCurrent(null); }}
        isOpen={true} onClose={() => { setView('list'); setCurrent(null); }}
        title={`📋 ${current.name}`}
        headerRight={<HStack>
          {current.category_name && <Badge colorScheme="teal" fontSize="sm" px={2}>{current.category_name}</Badge>}
          <Button size="sm" leftIcon={<FiEdit2 size={12} />} variant="outline" onClick={() => openEdit(current)}>Edit</Button>
        </HStack>}>
        <Box maxW="900px" mx="auto" w="100%">
          <ProcedureViewer procedure={current} />
        </Box>
      </PageShell>
    );
  }

  // ── Editor ──
  if (view === 'edit' && form) {
    const companyMatches = companies.filter(c => !form.companies.find(x => x.id === c.id));
    return (
      <PageShell fullPage={fullPage} onBack={() => { setView('list'); setForm(null); setCurrent(null); fetchList(); }}
        isOpen={true} onClose={() => { setView('list'); setForm(null); setCurrent(null); fetchList(); }}
        title={current ? `Edit: ${current.name}` : 'New Procedure'}>
        <Box maxW="900px" mx="auto" w="100%">
          <VStack align="stretch" spacing={3}>
            <HStack spacing={3} align="flex-end">
              <FormControl flex={2} isRequired><FormLabel fontSize="xs" mb={0.5}>Procedure Name</FormLabel>
                <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
              <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Category</FormLabel>
                <Select size="sm" value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))}>
                  <option value="">—</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select></FormControl>
              <HStack>
                <Input size="sm" w="150px" placeholder="Add category…" value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addCategory(); }} />
                <Button size="sm" variant="outline" onClick={addCategory} isDisabled={!newCatName.trim()}>Add</Button>
              </HStack>
            </HStack>
            <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
              <Textarea size="sm" rows={2} value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={0.5}>Companies involved (clients or suppliers)</FormLabel>
              {form.companies.length > 0 && (
                <Wrap spacing={1} mb={1.5}>
                  {form.companies.map(c => (
                    <Tag key={c.id} size="sm" colorScheme="blue" variant="subtle">
                      <TagLabel>{c.name}</TagLabel>
                      <TagCloseButton onClick={() => setForm(p => ({ ...p, companies: p.companies.filter(x => x.id !== c.id) }))} />
                    </Tag>
                  ))}
                </Wrap>
              )}
              <Select size="sm" maxW="360px" value=""
                onChange={e => {
                  const c = companies.find(x => String(x.id) === e.target.value);
                  if (c) setForm(p => ({ ...p, companies: [...p.companies, { id: c.id, name: c.name }] }));
                }}>
                <option value="">+ Add a company…</option>
                {companyMatches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormControl>
            <HStack justify="flex-end">
              <Button size="sm" colorScheme="teal" onClick={saveHeader} isLoading={saving}>
                {current ? 'Save Details' : 'Create Procedure'}
              </Button>
            </HStack>

            {/* ── Steps ── */}
            <Box borderTop="2px" borderColor="gray.100" pt={3}>
              <Flex align="center" justify="space-between" mb={2}>
                <Text fontSize="13px" fontWeight="700">Steps {current ? `(${(current.steps || []).length})` : ''}</Text>
                <Button size="xs" colorScheme="teal" variant="outline" leftIcon={<FiPlus size={11} />}
                  onClick={() => openStepForm(null)} isDisabled={!!stepForm}>Add Step</Button>
              </Flex>
              {!current && !stepForm && (
                <Text fontSize="11px" color="gray.400">Save the procedure details first, or add a step (it will save automatically).</Text>
              )}

              {(current?.steps || []).map((s, i) => {
                const T = (STEP_TYPES.find(t => t.key === s.step_type) || STEP_TYPES[0]).icon;
                return (
                  <Flex key={s.id} align="center" gap={2} border="1px" borderColor="gray.200" borderRadius="md" p={2} mb={1.5}
                    _hover={{ borderColor: 'teal.300' }}>
                    <Text fontSize="12px" fontWeight="800" color="teal.600" w="22px">{i + 1}</Text>
                    <T size={13} />
                    {/* Click the step body to view the full step + asset */}
                    <Box flex={1} minW={0} cursor="pointer" onClick={() => setViewStep({ ...s, _index: i })}
                      title="Click to view this step">
                      <Text fontSize="12px" fontWeight="600" isTruncated>{s.title || s.description || '(step)'}</Text>
                      <Text fontSize="9px" color="gray.400" isTruncated>
                        {s.step_type === 'link' && s.link_url}
                        {s.step_type === 'image' && (s.file_name || 'image')}
                        {s.step_type === 'document' && `${s.doc_kind === 'example' ? 'Example' : 'Procedure doc'}: ${s.file_name || s.document_name || ''}`}
                        {s.step_type === 'password' && `Password: ${s.password_name || ''}`}
                      </Text>
                    </Box>
                    <Tooltip label="View"><IconButton size="xs" variant="ghost" icon={<FiEye size={11} />} aria-label="view"
                      onClick={() => setViewStep({ ...s, _index: i })} /></Tooltip>
                    <Tooltip label="Edit"><IconButton size="xs" variant="ghost" colorScheme="teal" icon={<FiEdit2 size={11} />} aria-label="edit"
                      onClick={() => openStepForm(s)} /></Tooltip>
                    <IconButton size="xs" variant="ghost" icon={<FiArrowUp size={11} />} aria-label="up" onClick={() => moveStep(i, -1)} isDisabled={i === 0} />
                    <IconButton size="xs" variant="ghost" icon={<FiArrowDown size={11} />} aria-label="down" onClick={() => moveStep(i, 1)} isDisabled={i === (current.steps.length - 1)} />
                    <IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiTrash2 size={11} />} aria-label="del" onClick={() => deleteStep(s)} />
                  </Flex>
                );
              })}

              {stepForm && (
                <Box border="1px" borderColor="teal.200" bg="teal.50" borderRadius="md" p={3} mt={2}
                  onPaste={handleStepPaste} tabIndex={-1}>
                  <HStack spacing={2} mb={2} flexWrap="wrap">
                    {STEP_TYPES.map(t => (
                      <Button key={t.key} size="xs" leftIcon={<t.icon size={11} />}
                        variant={stepForm.step_type === t.key ? 'solid' : 'outline'} colorScheme="teal"
                        onClick={() => setStepForm(p => ({ ...p, step_type: t.key }))}>{t.label}</Button>
                    ))}
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Step Title (optional)</FormLabel>
                        <Input size="sm" bg="white" value={stepForm.title} onChange={e => setStepForm(p => ({ ...p, title: e.target.value }))} /></FormControl>
                    </HStack>
                    <FormControl><FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
                      <Textarea size="sm" bg="white" rows={2} value={stepForm.description}
                        onChange={e => setStepForm(p => ({ ...p, description: e.target.value }))} /></FormControl>

                    {stepForm.step_type === 'link' && (
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Link URL</FormLabel>
                        <Input size="sm" bg="white" placeholder="https://…" value={stepForm.link_url}
                          onChange={e => setStepForm(p => ({ ...p, link_url: e.target.value }))} /></FormControl>
                    )}

                    {stepForm.step_type === 'image' && (
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Image (shown inline — e.g. a snapshot you created)</FormLabel>
                        <HStack align="stretch" spacing={2}>
                          <Button as="label" size="xs" leftIcon={<FiUpload size={11} />} variant="outline" bg="white"
                            cursor="pointer" isLoading={uploading} alignSelf="center">
                            {stepForm.file_name || 'Choose image…'}
                            <input type="file" accept="image/*" hidden
                              onChange={e => e.target.files?.[0] && uploadStepFile(e.target.files[0])} />
                          </Button>
                          {/* Paste target: click, then Ctrl+V a snapshot — it
                              uploads and saves as the step image directly. */}
                          <Flex flex={1} minH="52px" border="2px dashed" borderColor={stepForm.file_id ? 'teal.300' : 'gray.300'}
                            borderRadius="md" bg="white" align="center" justify="center" px={3}
                            tabIndex={0} onPaste={handleStepPaste} cursor="text"
                            _focus={{ borderColor: 'teal.400', outline: 'none' }}>
                            {uploading ? (
                              <Text fontSize="11px" color="gray.500">Uploading snapshot…</Text>
                            ) : stepForm.file_id ? (
                              <Text fontSize="11px" color="teal.700">✓ {stepForm.file_name} — paste again to replace</Text>
                            ) : (
                              <Text fontSize="11px" color="gray.500">📸 Click here and press <b>Ctrl+V</b> to paste a snapshot</Text>
                            )}
                          </Flex>
                        </HStack>
                      </FormControl>
                    )}

                    {stepForm.step_type === 'document' && (
                      <>
                        <RadioGroup value={stepForm.doc_source} onChange={v => setStepForm(p => ({ ...p, doc_source: v, file_id: '', file_name: '', document_id: '', document_name: '' }))}>
                          <Stack direction="row" fontSize="12px">
                            <Radio value="upload" size="sm">Upload a file</Radio>
                            <Radio value="project" size="sm">Pull from a project's documents</Radio>
                          </Stack>
                        </RadioGroup>
                        {stepForm.doc_source === 'upload' ? (
                          <Button as="label" size="xs" leftIcon={<FiUpload size={11} />} variant="outline" bg="white"
                            cursor="pointer" isLoading={uploading} w="fit-content">
                            {stepForm.file_name || 'Choose file…'}
                            <input type="file" hidden onChange={e => e.target.files?.[0] && uploadStepFile(e.target.files[0])} />
                          </Button>
                        ) : (
                          <HStack>
                            <Select size="xs" bg="white" maxW="240px" value={stepForm.project_id}
                              onChange={e => setStepForm(p => ({ ...p, project_id: e.target.value, document_id: '' }))}>
                              <option value="">Choose project…</option>
                              {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.project_name || pr.name}</option>)}
                            </Select>
                            <Select size="xs" bg="white" maxW="280px" value={stepForm.document_id}
                              isDisabled={!stepForm.project_id}
                              onChange={e => setStepForm(p => ({ ...p, document_id: e.target.value }))}>
                              <option value="">Choose document…</option>
                              {projectDocs.map(d => <option key={d.id} value={d.id}>{d.name} {d.version ? `v${d.version}` : ''}</option>)}
                            </Select>
                          </HStack>
                        )}
                        <RadioGroup value={stepForm.doc_kind} onChange={v => setStepForm(p => ({ ...p, doc_kind: v }))}>
                          <Stack direction="row" fontSize="12px">
                            <Radio value="procedure" size="sm">Procedure document (the working file)</Radio>
                            <Radio value="example" size="sm">Example (a completed version — optional to use)</Radio>
                          </Stack>
                        </RadioGroup>
                      </>
                    )}

                    {stepForm.step_type === 'password' && (
                      <FormControl>
                        <FormLabel fontSize="xs" mb={0.5}>Password Safe entry (this procedure's tasks and companies)</FormLabel>
                        {(() => {
                          const viaTask = safeEntries.filter(pe => pe.via_task);
                          const viaCompany = safeEntries.filter(pe => !pe.via_task);
                          const opt = (pe) => <option key={pe.id} value={pe.id}>{pe.name} — {pe.company_name || 'no company'}{pe.project_name ? ` · ${pe.project_name}` : ''}</option>;
                          return (
                            <>
                              <Select size="sm" bg="white" maxW="420px" value={stepForm.password_entry_id}
                                onChange={e => setStepForm(p => ({ ...p, password_entry_id: e.target.value }))}>
                                <option value="">Choose…</option>
                                {viaTask.length > 0 && <optgroup label="Attached to this procedure's tasks">{viaTask.map(opt)}</optgroup>}
                                {viaCompany.length > 0 && <optgroup label="Company-wide entries of this procedure's companies">{viaCompany.map(opt)}</optgroup>}
                              </Select>
                              {safeEntries.length === 0 && (
                                <Text fontSize="10px" color="gray.500" mt={1}>
                                  {form.companies.length
                                    ? "No Password Safe entry you can see is attached to this procedure's tasks or company-wide for its companies."
                                    : 'This procedure has no companies yet: attach it to a task (its companies are filled in automatically) or add a company above, then save.'}
                                </Text>
                              )}
                            </>
                          );
                        })()}
                      </FormControl>
                    )}

                    <HStack justify="flex-end">
                      <Button size="xs" variant="ghost" onClick={() => { setStepForm(null); setEditingStepId(null); }}>Cancel</Button>
                      <Button size="xs" colorScheme="teal" onClick={addStep} isLoading={saving}>{editingStepId ? 'Save Step' : 'Add Step'}</Button>
                    </HStack>
                  </VStack>
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
        {/* Single-step viewer: full step + asset (image inline, link, doc, password) */}
        {viewStep && (
          <Modal isOpen={true} onClose={() => setViewStep(null)} size="2xl" scrollBehavior="inside" isCentered>
            <ModalOverlay bg="blackAlpha.400" />
            <ModalContent maxH="85vh">
              <ModalHeader fontSize="md">Step {viewStep._index + 1}{viewStep.title ? ` — ${viewStep.title}` : ''}</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={4} overflowY="auto" onWheel={e => e.stopPropagation()}>
                <ProcedureViewer procedure={{ steps: [viewStep] }} />
              </ModalBody>
              <ModalFooter>
                <Button size="sm" mr={2} variant="outline" colorScheme="teal" leftIcon={<FiEdit2 size={12} />}
                  onClick={() => { const st = viewStep; setViewStep(null); openStepForm(st); }}>Edit Step</Button>
                <Button size="sm" onClick={() => setViewStep(null)}>Close</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
      </PageShell>
    );
  }

  // ── List ──
  const qq = search.trim().toLowerCase();
  const visible = procedures.filter(p => !qq
    || (p.name || '').toLowerCase().includes(qq)
    || (p.description || '').toLowerCase().includes(qq)
    || (p.companies || []).some(c => (c.name || '').toLowerCase().includes(qq)));

  return (
    <PageShell fullPage={fullPage} onBack={onBack} isOpen={isOpen} onClose={onClose} title="📋 Procedures">
      <Flex mb={3} gap={2} flexWrap="wrap" align="flex-end">
        <FormControl w="220px"><FormLabel fontSize="xs" mb={0.5}>View</FormLabel>
          <Select size="sm" value={versionView} onChange={e => setVersionView(e.target.value)}>
            <option value="current">Show current procedures</option>
            <option value="previous">Show Previous Versions</option>
            <option value="deleted">Show Deleted Procedures</option>
          </Select></FormControl>
        <FormControl w="200px"><FormLabel fontSize="xs" mb={0.5}>Category</FormLabel>
          <Select size="sm" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select></FormControl>
        <FormControl w="260px"><FormLabel fontSize="xs" mb={0.5}>Company</FormLabel>
          <Select size="sm" value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
            <option value="">All companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select></FormControl>
        <FormControl w="220px"><FormLabel fontSize="xs" mb={0.5}>Search</FormLabel>
          <Input size="sm" value={search} onChange={e => setSearch(e.target.value)} /></FormControl>
        <Box flex={1} />
        <Button size="sm" colorScheme="teal" leftIcon={<FiPlus />} onClick={() => openEdit(null)}>New Procedure</Button>
      </Flex>

      {loading && procedures.length === 0 ? (
        <Flex justify="center" py={10}><Spinner /></Flex>
      ) : visible.length === 0 ? (
        <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>No procedures{qq ? ' match this search' : ' yet — create the first one'}.</Text>
      ) : (
        <VStack spacing={2} align="stretch">
          {visible.map(p => (
            <Flex key={p.id} border="1px" borderColor="gray.200" borderRadius="md" p={3} align="center" gap={3}
              _hover={{ borderColor: 'teal.300', bg: 'teal.50' }} cursor="pointer" onClick={() => openView(p)}>
              <FiClipboard size={16} />
              <Box flex={1} minW={0}>
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontSize="14px" fontWeight="700">{p.name}</Text>
                  {p.version_name && <Badge colorScheme={p.is_version ? 'gray' : 'blue'} fontSize="8px">{p.version_name}</Badge>}
                  {p.category_name && <Badge colorScheme="teal" fontSize="8px">{p.category_name}</Badge>}
                  <Badge colorScheme="gray" fontSize="8px">{p.step_count} step{p.step_count === 1 ? '' : 's'}</Badge>
                  {p.created_by_name && <Text fontSize="9px" color="gray.500">by {p.created_by_name}</Text>}
                  {versionView === 'deleted' && p.deleted_at && (
                    <Badge colorScheme="red" fontSize="8px">DELETED{p.deleted_by_name ? ` by ${p.deleted_by_name}` : ''}</Badge>
                  )}
                  {versionView === 'previous' && p.superseded_to && (
                    <Badge colorScheme="orange" fontSize="8px" cursor="pointer"
                      onClick={e => { e.stopPropagation(); openView({ id: p.superseded_to.id }); }}>
                      Superseded to {p.superseded_to.version_name} →
                    </Badge>
                  )}
                </HStack>
                {p.description && <Text fontSize="11px" color="gray.500" noOfLines={1}>{p.description}</Text>}
                {(p.companies || []).length > 0 && (
                  <Wrap spacing={1} mt={1}>
                    {p.companies.map(c => <Badge key={c.id} colorScheme="blue" fontSize="8px" variant="subtle">{c.name}</Badge>)}
                  </Wrap>
                )}
              </Box>
              <HStack spacing={1} onClick={e => e.stopPropagation()}>
                <Tooltip label="View / follow"><IconButton size="xs" variant="ghost" icon={<FiEye size={12} />} aria-label="view" onClick={() => openView(p)} /></Tooltip>
                {versionView === 'current' && (<>
                  <Tooltip label="Edit"><IconButton size="xs" variant="ghost" colorScheme="teal" icon={<FiEdit2 size={12} />} aria-label="edit" onClick={() => openEdit(p)} /></Tooltip>
                  <Tooltip label="Delete (retained for audit)"><IconButton size="xs" variant="ghost" colorScheme="red" icon={<FiTrash2 size={12} />} aria-label="delete" onClick={() => deleteProcedure(p)} /></Tooltip>
                </>)}
                {versionView === 'deleted' && (
                  <Tooltip label="Un-delete (SITAC restore — deletion evidence retained)">
                    <IconButton size="xs" variant="ghost" colorScheme="green" icon={<FiRotateCcw size={12} />} aria-label="restore"
                      onClick={async () => {
                        try { await api.post(`/procedures/${p.id}/restore`); fetchList(); toast({ title: 'Restored', status: 'success', duration: 1500 }); } catch (e) {}
                      }} />
                  </Tooltip>
                )}
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}
      <ConfirmDialog isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Procedure"
        message={`Are you sure you want to delete procedure \u201C${deleteTarget?.name}\u201D? Deleted procedures are retained for audit and remain viewable under \u201CShow Deleted Procedures\u201D.`}
        confirmLabel="DELETE" onConfirm={confirmDeleteProcedure} />
    </PageShell>
  );
}
